/**
 * ADMIN Change Requests Page
 * ADMIN can view all change requests and approve/reject them
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiCheckCircle, FiClock, FiXCircle, FiEye, FiCheck, FiX } from 'react-icons/fi';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { AdminLayout } from '../../components/admin';
import { apiClient } from '../../services/api';
import { useDialog, prompt } from '../../hooks/useDialog';
import toast from 'react-hot-toast';

interface ChangeRequest {
  id: string;
  userId: string;
  type: 'CREATE' | 'UPDATE' | 'COMPANY_IMAGE_ADD' | 'COMPANY_IMAGE_DELETE' | 'COMPANY_IMAGE_UPDATE';
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  requestedData: any;
  currentData?: any;
  changedFields?: Record<string, { old: any; new: any }>;
  userNotes?: string;
  adminNotes?: string;
  createdAt: string;
  reviewedAt?: string;
  companyId?: string;
  user?: {
    email: string;
    firstName: string;
    lastName: string;
  };
  company?: {
    nameTh: string;
  };
}

const ChangeRequestsPage: React.FC = () => {
  const { showConfirm, showPrompt, ConfirmDialog, AlertDialog } = useDialog();
  const [requests, setRequests] = useState<ChangeRequest[]>([]);
  const [totalCount, setTotalCount] = useState(0); // Total count for all requests
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('PENDING');
  const [typeFilter, setTypeFilter] = useState<string>('ALL');
  const [selectedRequest, setSelectedRequest] = useState<ChangeRequest | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Helper: แปลงชื่อฟิลด์เป็นภาษาไทย
  const getFieldNameThai = (key: string): string => {
    const fieldNames: Record<string, string> = {
      nameTh: 'ชื่อบริษัท (ไทย)',
      nameEn: 'ชื่อบริษัท (English)',
      description: 'คำอธิบาย',
      phone1: 'เบอร์โทรศัพท์ 1',
      phone2: 'เบอร์โทรศัพท์ 2',
      email: 'อีเมล',
      lineId: 'Line ID',
      website: 'เว็บไซต์',
      address: 'ที่อยู่',
      province: 'จังหวัด',
      district: 'เขต/อำเภอ',
      postalCode: 'รหัสไปรษณีย์',
      companySize: 'ขนาดบริษัท',
      establishedYear: 'ปีที่ก่อตั้ง',
      licenseNumber: 'เลขใบอนุญาต',
      taxId: 'เลขประจำตัวผู้เสียภาษี',
      serviceTypes: 'ประเภทบริการ',
      serviceAreas: 'พื้นที่ให้บริการ',
    };
    return fieldNames[key] || key;
  };

  // Helper: format ค่าให้อ่านง่าย
  const formatValue = (value: any, fieldKey?: string): string => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'ใช่' : 'ไม่';

    // Special handling for galleryImages
    if (fieldKey === 'galleryImages' && Array.isArray(value)) {
      if (value.length === 0) return 'ไม่มีรูปภาพ';
      return `${value.length} รูปภาพ: ${value.map((img: any) =>
        `${img.caption || 'ไม่มีคำอธิบาย'} (${img.displayOrder || 0})`
      ).join(', ')}`;
    }

    // Handle array of primitives
    if (Array.isArray(value)) {
      if (value.length === 0) return '-';
      // Check if array contains objects
      if (typeof value[0] === 'object') {
        return JSON.stringify(value, null, 2);
      }
      return value.join(', ');
    }

    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  // Helper: ตรวจสอบว่าฟิลด์นี้เปลี่ยนหรือไม่
  const isFieldChanged = (key: string): boolean => {
    if (!selectedRequest?.changedFields) return false;
    return key in selectedRequest.changedFields;
  };

  useEffect(() => {
    loadRequests();
    loadTotalCount(); // Load total count on mount
  }, [statusFilter, typeFilter]);

  const loadRequests = async () => {
    try {
      setLoading(true);
      const params: any = { limit: 100 };
      if (statusFilter !== 'ALL') {
        params.status = statusFilter;
      }
      if (typeFilter !== 'ALL') {
        params.type = typeFilter;
      }
      const response = await apiClient.get('/api/admin/change-requests/', { params });
      setRequests(response.data.items || []);
    } catch (error) {
      console.error('Failed to load requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTotalCount = async () => {
    try {
      // Load all requests without filters to get total count
      const response = await apiClient.get('/api/admin/change-requests/', { params: { limit: 100 } });
      setTotalCount(response.data.items?.length || 0);
    } catch (error) {
      console.error('Failed to load total count:', error);
      setTotalCount(0);
    }
  };

  const handleViewDetail = (request: ChangeRequest) => {
    setSelectedRequest(request);
    setShowDetailModal(true);
  };

  const handleApprove = async (requestId: string, adminNotes?: string) => {
    await showConfirm(
      {
        title: 'ยืนยันการอนุมัติ',
        message: 'คุณต้องการอนุมัติคำขอนี้ใช่หรือไม่?',
        confirmText: 'อนุมัติ',
        cancelText: 'ยกเลิก',
        variant: 'success'
      },
      async () => {
        setActionLoading(true);
        try {
          await apiClient.put(`/api/admin/change-requests/${requestId}/approve`, {
            adminNotes: adminNotes || undefined
          });
          toast.success('อนุมัติคำขอสำเร็จ');
          setShowDetailModal(false);
          await loadRequests();
        } catch (error: any) {
          console.error('Failed to approve request:', error);
          toast.error(error.response?.data?.message || 'ไม่สามารถอนุมัติคำขอได้');
        } finally {
          setActionLoading(false);
        }
      }
    );
  };

  const handleReject = async (requestId: string) => {
    const adminNotes = await prompt(
      'กรุณาระบุเหตุผลในการปฏิเสธ',
      'ปฏิเสธคำขอ',
      {
        placeholder: 'ระบุเหตุผล...',
        variant: 'warning',
        confirmText: 'ปฏิเสธ',
        cancelText: 'ยกเลิก'
      }
    );

    if (!adminNotes || !adminNotes.trim()) {
      if (adminNotes !== null) {
        toast.error('กรุณาระบุเหตุผลในการปฏิเสธ');
      }
      return;
    }

    setActionLoading(true);
    try {
      await apiClient.put(`/api/admin/change-requests/${requestId}/reject`, {
        adminNotes: adminNotes.trim()
      });
      toast.success('ปฏิเสธคำขอสำเร็จ');
      setShowDetailModal(false);
      await loadRequests();
    } catch (error: any) {
      console.error('Failed to reject request:', error);
      toast.error(error.response?.data?.message || 'ไม่สามารถปฏิเสธคำขอได้');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'PENDING':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <FiClock className="mr-1" /> รอดำเนินการ
          </span>
        );
      case 'APPROVED':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <FiCheckCircle className="mr-1" /> อนุมัติแล้ว
          </span>
        );
      case 'REJECTED':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <FiXCircle className="mr-1" /> ปฏิเสธ
          </span>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">จัดการคำขอเปลี่ยนแปลง</h1>
          <p className="text-gray-600">อนุมัติ/ปฏิเสธคำขอเพิ่ม/แก้ไขข้อมูลบริษัท (ทั้งหมด {totalCount} รายการ)</p>
        </div>

        {/* Tabs - Status Filter */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setStatusFilter('PENDING')}
                className={`${
                  statusFilter === 'PENDING'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
              >
                รอดำเนินการ
              </button>
              <button
                onClick={() => setStatusFilter('APPROVED')}
                className={`${
                  statusFilter === 'APPROVED'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
              >
                อนุมัติแล้ว
              </button>
              <button
                onClick={() => setStatusFilter('REJECTED')}
                className={`${
                  statusFilter === 'REJECTED'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
              >
                ปฏิเสธแล้ว
              </button>
              <button
                onClick={() => setStatusFilter('ALL')}
                className={`${
                  statusFilter === 'ALL'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
              >
                ทั้งหมด
              </button>
            </nav>
          </div>
        </div>

        {/* Type Filter */}
        <div className="mb-6">
          <div className="flex items-center space-x-4 flex-wrap gap-2">
            <label className="text-sm font-medium text-gray-700">ประเภท:</label>
            <div className="flex space-x-2 flex-wrap gap-2">
              <Button
                variant={typeFilter === 'ALL' ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setTypeFilter('ALL')}
              >
                ทั้งหมด
              </Button>
              <Button
                variant={typeFilter === 'CREATE' ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setTypeFilter('CREATE')}
              >
                เพิ่มบริษัทใหม่
              </Button>
              <Button
                variant={typeFilter === 'UPDATE' ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setTypeFilter('UPDATE')}
              >
                แก้ไขข้อมูลบริษัท
              </Button>
              <Button
                variant={typeFilter === 'COMPANY_IMAGE_ADD' ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setTypeFilter('COMPANY_IMAGE_ADD')}
              >
                เพิ่มรูป Gallery
              </Button>
              <Button
                variant={typeFilter === 'COMPANY_IMAGE_DELETE' ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setTypeFilter('COMPANY_IMAGE_DELETE')}
              >
                ลบรูป Gallery
              </Button>
              <Button
                variant={typeFilter === 'COMPANY_IMAGE_UPDATE' ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setTypeFilter('COMPANY_IMAGE_UPDATE')}
              >
                แก้ไขรูป Gallery
              </Button>
            </div>
          </div>
        </div>

        {/* Requests Table */}
        <Card>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ประเภท</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">บริษัท</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ผู้ส่งคำขอ</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">สถานะ</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">วันที่ส่ง</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">จัดการ</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {requests.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                      ไม่พบคำขอ
                    </td>
                  </tr>
                ) : (
                  requests.map(request => (
                    <tr key={request.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded ${
                          request.type === 'CREATE' ? 'bg-blue-100 text-blue-800' :
                          request.type === 'UPDATE' ? 'bg-purple-100 text-purple-800' :
                          request.type === 'COMPANY_IMAGE_ADD' ? 'bg-green-100 text-green-800' :
                          request.type === 'COMPANY_IMAGE_DELETE' ? 'bg-red-100 text-red-800' :
                          request.type === 'COMPANY_IMAGE_UPDATE' ? 'bg-orange-100 text-orange-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {request.type === 'CREATE' ? 'เพิ่มบริษัท' :
                           request.type === 'UPDATE' ? 'แก้ไขบริษัท' :
                           request.type === 'COMPANY_IMAGE_ADD' ? 'เพิ่มรูป' :
                           request.type === 'COMPANY_IMAGE_DELETE' ? 'ลบรูป' :
                           request.type === 'COMPANY_IMAGE_UPDATE' ? 'แก้ไขรูป' :
                           request.type}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {request.requestedData?.nameTh || request.company?.nameTh || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {request.user?.firstName} {request.user?.lastName}
                        </div>
                        <div className="text-xs text-gray-500">{request.user?.email || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(request.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(request.createdAt).toLocaleDateString('th-TH')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewDetail(request)}
                        >
                          <FiEye className="mr-1" /> ดูรายละเอียด
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Detail Modal */}
        {showDetailModal && selectedRequest && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black bg-opacity-50"
              onClick={() => setShowDetailModal(false)}
            />

            {/* Modal */}
            <div className="flex min-h-screen items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="relative w-full max-w-3xl"
              >
                <Card className="p-6 max-h-[90vh] overflow-y-auto">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">รายละเอียดคำขอ</h2>
                      <p className="text-sm text-gray-600 mt-1">
                        {selectedRequest.type === 'CREATE' ? 'เพิ่มบริษัทใหม่' :
                         selectedRequest.type === 'UPDATE' ? 'แก้ไขข้อมูลบริษัท' :
                         selectedRequest.type === 'COMPANY_IMAGE_ADD' ? 'เพิ่มรูปภาพ Gallery' :
                         selectedRequest.type === 'COMPANY_IMAGE_DELETE' ? 'ลบรูปภาพ Gallery' :
                         selectedRequest.type === 'COMPANY_IMAGE_UPDATE' ? 'แก้ไขรูปภาพ Gallery' :
                         selectedRequest.type}
                      </p>
                    </div>
                    <button
                      onClick={() => setShowDetailModal(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <FiX className="w-6 h-6" />
                    </button>
                  </div>

                  {/* Request Info */}
                  <div className="mb-6 space-y-3">
                    <div className="flex items-center space-x-4">
                      <span className="text-sm font-medium text-gray-500">สถานะ:</span>
                      {getStatusBadge(selectedRequest.status)}
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">ผู้ส่งคำขอ:</span>
                      <span className="ml-2 text-sm text-gray-900">
                        {selectedRequest.user?.email}
                      </span>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">วันที่ส่งคำขอ:</span>
                      <span className="ml-2 text-sm text-gray-900">
                        {new Date(selectedRequest.createdAt).toLocaleString('th-TH')}
                      </span>
                    </div>
                  </div>

                  {/* Before/After Comparison for UPDATE */}
                  {selectedRequest.type === 'UPDATE' && selectedRequest.currentData && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">เปรียบเทียบการเปลี่ยนแปลง</h3>

                      {/* Summary Badge */}
                      {selectedRequest.changedFields && Object.keys(selectedRequest.changedFields).length > 0 && (
                        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                          <p className="text-sm font-medium text-yellow-900">
                            เปลี่ยนแปลง {Object.keys(selectedRequest.changedFields).length} ฟิลด์: {' '}
                            <span className="font-normal">
                              {Object.keys(selectedRequest.changedFields).map(k => getFieldNameThai(k)).join(', ')}
                            </span>
                          </p>
                        </div>
                      )}

                      {/* Comparison Grid */}
                      <div className="grid grid-cols-2 gap-4">
                        {/* BEFORE - Current Data */}
                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                            <FiClock className="mr-2 h-4 w-4" />
                            ข้อมูลปัจจุบัน (Before)
                          </h4>
                          <div className="space-y-2">
                            {Object.entries(selectedRequest.currentData)
                              .filter(([key]) => key in (selectedRequest.requestedData || {}))
                              .map(([key, value]) => (
                                <div
                                  key={key}
                                  className={`p-2 rounded ${
                                    isFieldChanged(key)
                                      ? 'bg-red-50 border border-red-200'
                                      : 'bg-white border border-gray-100'
                                  }`}
                                >
                                  <p className="text-xs font-medium text-gray-500">{getFieldNameThai(key)}</p>
                                  <p className={`text-sm mt-1 ${
                                    isFieldChanged(key) ? 'text-red-700 line-through' : 'text-gray-900'
                                  }`}>
                                    {formatValue(value, key)}
                                  </p>
                                </div>
                              ))}
                          </div>
                        </div>

                        {/* AFTER - Requested Data */}
                        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                          <h4 className="text-sm font-semibold text-blue-900 mb-3 flex items-center">
                            <FiCheckCircle className="mr-2 h-4 w-4" />
                            ข้อมูลที่ขอเปลี่ยน (After)
                          </h4>
                          <div className="space-y-2">
                            {Object.entries(selectedRequest.requestedData).map(([key, value]) => (
                              <div
                                key={key}
                                className={`p-2 rounded ${
                                  isFieldChanged(key)
                                    ? 'bg-green-50 border border-green-300'
                                    : 'bg-white border border-gray-100'
                                }`}
                              >
                                <p className="text-xs font-medium text-gray-500">{getFieldNameThai(key)}</p>
                                <p className={`text-sm mt-1 font-medium ${
                                  isFieldChanged(key) ? 'text-green-700' : 'text-gray-900'
                                }`}>
                                  {formatValue(value, key)}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Gallery Image Request Details */}
                  {(selectedRequest.type === 'COMPANY_IMAGE_ADD' ||
                    selectedRequest.type === 'COMPANY_IMAGE_DELETE' ||
                    selectedRequest.type === 'COMPANY_IMAGE_UPDATE') && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">
                        รายละเอียดรูปภาพ
                      </h3>

                      {/* Gallery Image Preview */}
                      {selectedRequest.requestedData?.imageUrl && (
                        <div className="mb-4">
                          <img
                            src={selectedRequest.requestedData.imageUrl}
                            alt={selectedRequest.requestedData.altText || 'Gallery image'}
                            className="max-w-md rounded-lg border-2 border-gray-300 shadow-sm"
                          />
                        </div>
                      )}

                      {/* Image Details */}
                      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                        <div className="space-y-2">
                          {selectedRequest.type === 'COMPANY_IMAGE_ADD' && (
                            <>
                              <div className="p-2 bg-white rounded border border-gray-100">
                                <p className="text-xs font-medium text-gray-500">คำอธิบาย (Caption)</p>
                                <p className="text-sm mt-1 text-gray-900">
                                  {selectedRequest.requestedData?.caption || '-'}
                                </p>
                              </div>
                              <div className="p-2 bg-white rounded border border-gray-100">
                                <p className="text-xs font-medium text-gray-500">Alt Text (SEO)</p>
                                <p className="text-sm mt-1 text-gray-900">
                                  {selectedRequest.requestedData?.altText || '-'}
                                </p>
                              </div>
                              <div className="p-2 bg-white rounded border border-gray-100">
                                <p className="text-xs font-medium text-gray-500">Display Order</p>
                                <p className="text-sm mt-1 text-gray-900">
                                  {selectedRequest.requestedData?.displayOrder ?? '-'}
                                </p>
                              </div>
                            </>
                          )}

                          {selectedRequest.type === 'COMPANY_IMAGE_DELETE' && (
                            <>
                              <div className="p-2 bg-white rounded border border-gray-100">
                                <p className="text-xs font-medium text-gray-500">รูปภาพที่จะลบ</p>
                                <p className="text-sm mt-1 text-gray-900">
                                  {selectedRequest.requestedData?.caption || 'ไม่มีคำอธิบาย'}
                                </p>
                              </div>
                            </>
                          )}

                          {selectedRequest.type === 'COMPANY_IMAGE_UPDATE' && (
                            <>
                              <div className="grid grid-cols-2 gap-4">
                                <div className="p-2 bg-white rounded border border-gray-100">
                                  <p className="text-xs font-medium text-gray-500">Caption (ก่อน)</p>
                                  <p className="text-sm mt-1 text-gray-700 line-through">
                                    {selectedRequest.requestedData?.currentCaption || '-'}
                                  </p>
                                </div>
                                <div className="p-2 bg-green-50 rounded border border-green-200">
                                  <p className="text-xs font-medium text-gray-500">Caption (หลัง)</p>
                                  <p className="text-sm mt-1 text-green-700 font-medium">
                                    {selectedRequest.requestedData?.newCaption || '-'}
                                  </p>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-4">
                                <div className="p-2 bg-white rounded border border-gray-100">
                                  <p className="text-xs font-medium text-gray-500">Alt Text (ก่อน)</p>
                                  <p className="text-sm mt-1 text-gray-700 line-through">
                                    {selectedRequest.requestedData?.currentAltText || '-'}
                                  </p>
                                </div>
                                <div className="p-2 bg-green-50 rounded border border-green-200">
                                  <p className="text-xs font-medium text-gray-500">Alt Text (หลัง)</p>
                                  <p className="text-sm mt-1 text-green-700 font-medium">
                                    {selectedRequest.requestedData?.newAltText || '-'}
                                  </p>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Requested Data for CREATE */}
                  {selectedRequest.type === 'CREATE' && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">ข้อมูลบริษัทใหม่</h3>
                      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                        <div className="space-y-2">
                          {Object.entries(selectedRequest.requestedData).map(([key, value]) => (
                            <div key={key} className="p-2 bg-white rounded border border-gray-100">
                              <p className="text-xs font-medium text-gray-500">{getFieldNameThai(key)}</p>
                              <p className="text-sm mt-1 text-gray-900">{formatValue(value, key)}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* User Notes */}
                  {selectedRequest.userNotes && (
                    <div className="mb-6">
                      <h3 className="text-sm font-semibold text-gray-900 mb-2">หมายเหตุจากผู้ใช้</h3>
                      <p className="text-sm text-gray-700 bg-blue-50 p-3 rounded">
                        {selectedRequest.userNotes}
                      </p>
                    </div>
                  )}

                  {/* Admin Notes */}
                  {selectedRequest.adminNotes && (
                    <div className="mb-6">
                      <h3 className="text-sm font-semibold text-gray-900 mb-2">หมายเหตุจาก Admin</h3>
                      <p className="text-sm text-gray-700 bg-yellow-50 p-3 rounded">
                        {selectedRequest.adminNotes}
                      </p>
                    </div>
                  )}

                  {/* Actions */}
                  {selectedRequest.status === 'PENDING' && (
                    <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                      <Button
                        variant="destructive"
                        onClick={() => handleReject(selectedRequest.id)}
                        disabled={actionLoading}
                        leftIcon={<FiX />}
                      >
                        ปฏิเสธ
                      </Button>
                      <Button
                        variant="success"
                        onClick={() => handleApprove(selectedRequest.id)}
                        disabled={actionLoading}
                        leftIcon={<FiCheck />}
                        loading={actionLoading}
                        loadingText="กำลังดำเนินการ..."
                      >
                        อนุมัติ
                      </Button>
                    </div>
                  )}
                </Card>
              </motion.div>
            </div>
          </div>
        )}

        {/* Dialog Components */}
        {ConfirmDialog}
        {AlertDialog}
      </div>
    </AdminLayout>
  );
};

export default ChangeRequestsPage;
